import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { matchRoute, parseLanding, type LandingQuery, type RouteId } from './lib/router';

interface RouteState {
  route: RouteId;
  /**
   * The ?state=/?error= query, parsed by the pure `parseLanding` in
   * lib/router.ts. Views read the OAuth landing result from here.
   */
  search: LandingQuery;
  /**
   * The raw fragment, '' or '#…'. Tracked as state rather than read off
   * `window.location` at render time, because a component that reads the
   * fragment during render is only correct by accident: nothing would
   * re-render it when the fragment alone changes. The Staff view addresses
   * its three tabs this way (lib/router.ts's `parseStaffTab`), and Layout's
   * owner-only nav entry already links `/mitarbeiter#administratoren`.
   */
  hash: string;
  navigate: (path: string, options?: { replace?: boolean }) => void;
  /**
   * Drops the query string from the URL without a navigation (review point B2).
   * Used by both OAuth landings so ?state=<jti> and ?error=… never enter the
   * history stack, are never in a Referer, and survive no reload.
   */
  clearSearch: () => void;
}

const RouteContext = createContext<RouteState | null>(null);

export function Router({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState(() => ({
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  }));

  useEffect(() => {
    const sync = () => setLocation({
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    });
    window.addEventListener('popstate', sync);
    // 'hashchange' is NOT redundant with 'popstate': editing only the fragment
    // in the address bar (or following a plain in-page anchor this app did not
    // intercept) fires hashchange and no popstate, so without this the Staff
    // view's tab would silently disagree with the URL that names it.
    window.addEventListener('hashchange', sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener('hashchange', sync);
    };
  }, []);

  const navigate = useCallback((path: string, options?: { replace?: boolean }) => {
    if (options?.replace) window.history.replaceState(null, '', path);
    else window.history.pushState(null, '', path);
    const url = new URL(path, window.location.origin);
    setLocation({ pathname: url.pathname, search: url.search, hash: url.hash });
  }, []);

  const clearSearch = useCallback(() => {
    // The fragment is preserved: this exists to drop ?state=<jti> from the
    // URL, not to move the user off the tab they are looking at.
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    setLocation((prev) => ({ ...prev, search: '' }));
  }, []);

  const value = useMemo<RouteState>(() => ({
    route: matchRoute(location.pathname),
    search: parseLanding(location.search),
    hash: location.hash,
    navigate,
    clearSearch,
  }), [location, navigate, clearSearch]);

  return <RouteContext.Provider value={value}>{children}</RouteContext.Provider>;
}

export function useRoute(): RouteState {
  const ctx = useContext(RouteContext);
  if (!ctx) throw new Error('useRoute outside <Router>');
  return ctx;
}
